import type { AccountCreateSchema } from '@liscio/api';

import type {
  CreateAccountBankAccountValues,
  CreateAccountFormValues,
} from './createAccountValidationSchema';

const isPresent = (value: unknown): value is string | number | boolean =>
  value !== undefined && value !== null && value !== '';

/**
 * Optional V5 account fields where the react-hook-form key matches `account_create_schema`.
 * Excludes encrypted fields, dependents, and bank rows — those are shaped differently.
 */
const OPTIONAL_ACCOUNT_FIELDS = [
  'website',
  'phone_number',
  'fax_number',
  'referral_source',
  'rating',
  'external_account_id',
  'address_line_1',
  'address_line_2',
  'address_city',
  'address_state',
  'address_zip',
  'address_country',
  'co_industry',
  'co_prior_accountant',
  'co_fiscal_yearend',
  'co_biggest_challenges',
  'co_biz_decision_tools_used',
  'co_expectations',
  'co_tech_accounting',
  'co_tech_expenses',
  'co_tech_time_trackings',
  'co_tech_payroll',
  'co_tech_billpay',
  'taxpayer_first_name',
  'taxpayer_middle_name',
  'taxpayer_last_name',
  'in_curr_filling_status',
  'in_self_birthdate',
  'in_self_driver_license',
  'in_self_license_issue_date',
  'in_self_license_expiry_date',
  'in_self_license_state',
  'in_sps_first_name',
  'in_sps_middle_name',
  'in_sps_last_name',
  'in_sps_birthdate',
  'in_sps_driver_license',
  'in_sps_license_issue_date',
  'in_sps_license_expiry_date',
  'in_sps_license_state',
  'in_sps_address_line_1',
  'in_sps_address_line_2',
  'in_sps_address_city',
  'in_sps_address_state',
  'in_sps_address_zip',
  'in_sps_address_country',
] as const satisfies ReadonlyArray<
  keyof AccountCreateSchema & keyof CreateAccountFormValues
>;

type OptionalAccountField = (typeof OPTIONAL_ACCOUNT_FIELDS)[number];

function pickPresentOptionalAccountFields(
  values: CreateAccountFormValues
): Pick<AccountCreateSchema, OptionalAccountField> {
  const picked = {} as Pick<AccountCreateSchema, OptionalAccountField>;

  for (const key of OPTIONAL_ACCOUNT_FIELDS) {
    const value = values[key];
    if (isPresent(value) && typeof value === 'string') {
      picked[key] = value;
    }
  }

  return picked;
}

function buildCoreCreateAccountFields(
  values: CreateAccountFormValues
): Pick<AccountCreateSchema, 'name' | 'entity_type' | 'status'> &
  Partial<Pick<AccountCreateSchema, 'stage' | 'is_restricted_account'>> {
  const isClientStatus = values.status?.toLowerCase() === 'client';

  const core: Pick<AccountCreateSchema, 'name' | 'entity_type' | 'status'> &
    Partial<Pick<AccountCreateSchema, 'stage' | 'is_restricted_account'>> = {
    name: values.name,
    entity_type: values.entity_type,
    status: values.status,
  };

  if (!isClientStatus && isPresent(values.stage)) {
    core.stage = values.stage;
  }

  if (values.is_restricted_account) {
    core.is_restricted_account = true;
  }

  return core;
}

function mapCoNumberOfEmployees(
  values: CreateAccountFormValues
): Pick<AccountCreateSchema, 'co_number_of_employees'> | undefined {
  if (!isPresent(values.co_number_of_employees)) {
    return undefined;
  }

  const parsed = Number.parseInt(values.co_number_of_employees, 10);
  if (Number.isNaN(parsed)) {
    return undefined;
  }

  return { co_number_of_employees: parsed };
}

type BankAccountApiFields = Pick<
  AccountCreateSchema,
  | 'in_bank_name'
  | 'in_bank_routing_number'
  | 'in_bank_account_type'
  | 'in_bank_website'
  | 'in_notes'
  | 'in_ach_is_autopay_confirmed'
>;

/** First bank row only; form field names differ from flat `in_*` API columns. */
function mapFirstBankAccountRow(
  bank: CreateAccountBankAccountValues | undefined
): Partial<BankAccountApiFields> {
  if (!bank) {
    return {};
  }

  const payload: Partial<BankAccountApiFields> = {};

  if (isPresent(bank.bank_name)) {
    payload.in_bank_name = bank.bank_name;
  }
  if (isPresent(bank.routing_number)) {
    payload.in_bank_routing_number = bank.routing_number;
  }
  if (isPresent(bank.account_type)) {
    payload.in_bank_account_type = bank.account_type;
  }
  if (isPresent(bank.web_site)) {
    payload.in_bank_website = bank.web_site;
  }
  if (isPresent(bank.notes)) {
    payload.in_notes = bank.notes;
  }
  if (bank.is_ach_autopay === true) {
    payload.in_ach_is_autopay_confirmed = true;
  }

  return payload;
}

/**
 * Builds the flat JSON body for `POST /api/v5/firms/accounts` from react-hook-form state.
 * Omits empty strings, form-only fields, and applies create-specific rules (e.g. no stage for Client).
 */
export function mapCreateAccountFormValuesToApiPayload(
  values: CreateAccountFormValues
): AccountCreateSchema {
  return {
    ...buildCoreCreateAccountFields(values),
    ...pickPresentOptionalAccountFields(values),
    ...mapCoNumberOfEmployees(values),
    ...mapFirstBankAccountRow(values.bankAccounts?.[0]),
  };
}
